import {
  AddressType,
  enumAddressType,
} from '@rematter/frontend-lib-api-client';
import {
  Checkbox,
  FieldGroup,
  Row,
  SelectCountry,
  TextField,
  cn,
} from '@rematter/ui-web';
import { countries } from 'countries-list';
import { useAtom } from 'jotai';
import keyBy from 'lodash/keyBy';

import { AddressFormState } from '../../_state/_types';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { StreetAddressField } from '~/shared/components/StreetAddressField';

export type AddressFormFieldsProps = {
  showNameField?: boolean;
  nameLabel?: string;
  namePlaceholder?: string;
  className?: string;
  autoFocus?: boolean;
};

const countriesByName = keyBy(
  Object.entries(countries).map(([code, data]) => ({ code, name: data.name })),
  (it) => it.name,
);

export const AddressFormFields = ({
  showNameField = true,
  nameLabel,
  namePlaceholder,
  className = '',
  autoFocus = true,
}: AddressFormFieldsProps) => {
  const t = useTranslations();

  const resolvedNameLabel =
    nameLabel ?? t.accountsView.form.fields.address.addressName;
  const resolvedNamePlaceholder =
    namePlaceholder ?? t.accountsView.form.fields.address.namePlaceholder;

  const [addressForm, updateAddressForm] = useAtom(
    accountEditingState.$addressForm,
  );

  const handleChange =
    <T extends keyof AddressFormState>(key: T) =>
    (value: AddressFormState[T]) => {
      updateAddressForm({ [key]: value });
    };

  const handleAddressChange =
    <T extends keyof AddressFormState['address']>(key: T) =>
    (value: AddressFormState['address'][T]) => {
      updateAddressForm({ address: { [key]: value } });
    };

  const fieldProps = <T extends keyof AddressFormState>(key: T) => ({
    value: addressForm[key],
    onChange: handleChange(key),
  });

  const addressFieldProps = <T extends keyof AddressFormState['address']>(
    key: T,
  ) => ({
    value: addressForm.address[key],
    onChange: handleAddressChange(key),
  });

  return (
    <div
      className={cn(
        '@AddressFormFields',
        cn('grid grid-cols-2 gap-4', className),
      )}
    >
      {showNameField && (
        <TextField
          label={resolvedNameLabel}
          placeholder={resolvedNamePlaceholder}
          isRequired
          autoFocus={autoFocus}
          {...fieldProps('name')}
        />
      )}

      <SelectEnum<AddressType>
        enumValue={{
          [enumAddressType.Standard]: enumAddressType.Standard,
          [enumAddressType.NonStandard]: enumAddressType.NonStandard,
          [enumAddressType.POBox]: enumAddressType.POBox,
        }}
        autoFocus={addressForm.address.addressType === enumAddressType.POBox}
        label={t.accountsView.form.fields.address.type}
        isRequired
        value={addressForm.address.addressType}
        onChange={handleAddressChange('addressType')}
        className={cn(!showNameField && 'col-span-2')}
      />

      {addressForm.address.addressType === enumAddressType.Standard && (
        <StreetAddressField
          isRequired
          className="col-span-2"
          value={{
            streetAddress: addressForm.address.streetAddress ?? '',
            city: addressForm.address.city ?? '',
            state: addressForm.address.state ?? '',
            postalCode: addressForm.address.postalCode ?? '',
            country: addressForm.address.country ?? '',
          }}
          onChange={(address) => {
            const countryCode = countriesByName[address.country]?.code;
            updateAddressForm({
              address: {
                addressType: addressForm.address.addressType,
                streetAddress: address.streetAddress,
                city: address.city,
                state: address.state,
                postalCode: address.postalCode,
                country: countryCode ?? '',
                coordinates: address.coordinates,
              },
            });
          }}
        />
      )}

      {addressForm.address.addressType === enumAddressType.NonStandard && (
        <TextField
          label={t.accountsView.form.fields.address.streetAddress}
          placeholder={
            t.accountsView.form.fields.address.streetAddressPlaceholder
          }
          className="col-span-2"
          isRequired
          {...addressFieldProps('streetAddress')}
        />
      )}

      {addressForm.address.addressType === enumAddressType.POBox && (
        <TextField
          label={t.accountsView.form.fields.address.poBoxNumber}
          isRequired
          {...addressFieldProps('streetAddress')}
        />
      )}

      <TextField
        label={t.accountsView.form.fields.address.addressLine2}
        placeholder={t.accountsView.form.fields.address.addressLine2Placeholder}
        className="col-span-2"
        {...addressFieldProps('streetAddress2')}
      />

      <TextField
        label={t.accountsView.form.fields.address.city}
        isRequired
        {...addressFieldProps('city')}
      />
      <TextField
        label={t.accountsView.form.fields.address.state}
        isRequired
        {...addressFieldProps('state')}
      />

      <TextField
        label={t.accountsView.form.fields.address.postalCode}
        isRequired
        {...addressFieldProps('postalCode')}
      />

      <SelectCountry
        label={t.accountsView.form.fields.address.country}
        placeholder={t.accountsView.form.fields.address.selectCountry}
        value={addressForm.address.country}
        onSelectionChange={handleAddressChange('country')}
        showFlags
      />
      <Row yEnd>
        <FieldGroup>
          <Checkbox
            className="col-span-2"
            isSelected={addressForm.address.isMailingAddress}
            onChange={handleAddressChange('isMailingAddress')}
          >
            {t.accountsView.form.fields.address.isMailingAddress}
          </Checkbox>
        </FieldGroup>
      </Row>
    </div>
  );
};
